import React, { useEffect, useState } from 'react';
import { X, Lightbulb, Loader2, RefreshCw, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import type { Language } from '../types';

interface DailyGuideData {
  id?: number;
  guideType: 'morning' | 'evening';
  dailyInsight: string;
  shortTermAction?: string;
  shortTermTimeframe?: string;
  shortTermCompleted?: boolean;
  mediumTermGoal?: string;
  mediumTermTimeframe?: string;
  mediumTermCompleted?: boolean;
  longTermVision?: string;
  longTermTimeframe?: string;
  longTermCompleted?: boolean;
  focusAreas?: string[];
  connectedValues?: string[];
  connectedGoals?: string[];
  recentMomentsCount?: number;
}

interface WeeklyRecapData {
  id: number;
  weekStart: Date;
  weekEnd: Date;
  oracleMessage: string;
  keyInsight: string;
  dominantEmotion?: string;
  topPattern?: string;
  recommendedAction?: string;
  weeklyScore?: number;
  entriesCount: number;
}

interface StoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'daily' | 'weekly';
  dailyGuide?: DailyGuideData | null;
  weeklyRecap?: WeeklyRecapData | null;
  onRefreshGuide?: () => void;
  onCompleteAction?: (actionType: 'short' | 'medium' | 'long', completed: boolean) => void;
  isRefreshing?: boolean;
  language: Language;
}

const TRANSLATIONS = {
  Italian: {
    yourGuide: 'La Tua Guida',
    morning: 'Guida Mattutina',
    evening: 'Guida Serale',
    today: 'Oggi',
    thisWeek: 'Questa Settimana',
    vision: 'Visione',
    weeklyOracle: 'Oracolo Settimanale',
    weekOf: 'Settimana del',
    dominantEmotion: 'Emozione Dominante',
    moments: 'momenti',
    keyPattern: 'Pattern Chiave',
    nextStep: 'Prossimo Passo',
    score: 'Punteggio',
    refresh: 'Aggiorna',
    tapToClose: 'Tocca per chiudere',
    generating: 'Sto creando la tua guida...',
    generatingSubtitle: 'Analizzando i tuoi momenti recenti',
    yourPlan: 'Il Tuo Piano',
  },
  English: {
    yourGuide: 'Your Guide',
    morning: 'Morning Guide',
    evening: 'Evening Guide',
    today: 'Today',
    thisWeek: 'This Week',
    vision: 'Vision',
    weeklyOracle: 'Weekly Oracle',
    weekOf: 'Week of',
    dominantEmotion: 'Dominant Emotion',
    moments: 'moments',
    keyPattern: 'Key Pattern',
    nextStep: 'Next Step',
    score: 'Score',
    refresh: 'Refresh',
    tapToClose: 'Tap to close',
    generating: 'Creating your guide...',
    generatingSubtitle: 'Analyzing your recent moments',
    yourPlan: 'Your Plan',
  },
  Spanish: {
    yourGuide: 'Tu Guía',
    morning: 'Guía Matutina',
    evening: 'Guía Vespertina',
    today: 'Hoy',
    thisWeek: 'Esta Semana',
    vision: 'Visión',
    weeklyOracle: 'Oráculo Semanal',
    weekOf: 'Semana del',
    dominantEmotion: 'Emoción Dominante',
    moments: 'momentos',
    keyPattern: 'Patrón Clave',
    nextStep: 'Próximo Paso',
    score: 'Puntuación',
    refresh: 'Actualizar',
    tapToClose: 'Toca para cerrar',
    generating: 'Creando tu guía...',
    generatingSubtitle: 'Analizando tus momentos recientes',
    yourPlan: 'Tu Plan',
  },
  German: {
    yourGuide: 'Dein Wegweiser',
    morning: 'Morgen-Wegweiser',
    evening: 'Abend-Wegweiser',
    today: 'Heute',
    thisWeek: 'Diese Woche',
    vision: 'Vision',
    weeklyOracle: 'Wöchentliches Orakel',
    weekOf: 'Woche vom',
    dominantEmotion: 'Dominante Emotion',
    moments: 'Momente',
    keyPattern: 'Schlüsselmuster',
    nextStep: 'Nächster Schritt',
    score: 'Punktzahl',
    refresh: 'Aktualisieren',
    tapToClose: 'Zum Schließen tippen',
    generating: 'Erstelle deinen Wegweiser...',
    generatingSubtitle: 'Analysiere deine letzten Momente',
    yourPlan: 'Dein Plan',
  },
  French: {
    yourGuide: 'Ton Guide',
    morning: 'Guide Matinal',
    evening: 'Guide du Soir',
    today: "Aujourd'hui",
    thisWeek: 'Cette Semaine',
    vision: 'Vision',
    weeklyOracle: 'Oracle Hebdomadaire',
    weekOf: 'Semaine du',
    dominantEmotion: 'Émotion Dominante',
    moments: 'moments',
    keyPattern: 'Pattern Clé',
    nextStep: 'Prochaine Étape',
    score: 'Score',
    refresh: 'Actualiser',
    tapToClose: 'Appuie pour fermer',
    generating: 'Création de ton guide...',
    generatingSubtitle: 'Analyse de tes moments récents',
    yourPlan: 'Ton Plan',
  },
};

const StoryModal: React.FC<StoryModalProps> = ({
  isOpen,
  onClose,
  type,
  dailyGuide,
  weeklyRecap,
  onRefreshGuide,
  onCompleteAction,
  isRefreshing = false,
  language,
}) => {
  const t = TRANSLATIONS[language] || TRANSLATIONS.English;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [progress, setProgress] = useState(0);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Auto-progress timer
  useEffect(() => {
    if (!isOpen) return;

    setProgress(0);
    setCurrentSlide(0);

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          // Move to next slide or close
          return prev;
        }
        return prev + 2;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [isOpen]);

  if (!isOpen) return null;

  // Calculate total slides dynamically based on content
  const getDailyGuideSlides = () => {
    if (!dailyGuide) return 1;
    let count = 1; // Always have insight slide
    if (dailyGuide.shortTermAction) count++;
    if (dailyGuide.mediumTermGoal) count++;
    if (dailyGuide.longTermVision) count++;
    return count;
  };

  const totalSlides = type === 'daily' ? getDailyGuideSlides() : 3;

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => Math.max(0, prev - 1));
    setProgress(0);
  };

  const handleNextSlide = () => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide((prev) => prev + 1);
      setProgress(0);
    } else {
      onClose();
    }
  };

  // Get the current background style for daily guide
  const getDailyBgClass = () => {
    if (type !== 'daily' || !dailyGuide) return 'bg-black';

    const slideTypes: Array<'insight' | 'short' | 'medium' | 'long'> = ['insight'];
    if (dailyGuide.shortTermAction) slideTypes.push('short');
    if (dailyGuide.mediumTermGoal) slideTypes.push('medium');
    if (dailyGuide.longTermVision) slideTypes.push('long');

    const slideType = slideTypes[currentSlide] || 'insight';
    const bgMap = {
      insight: 'bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-600',
      short: 'bg-gradient-to-br from-amber-500 via-orange-500 to-red-500',
      medium: 'bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700',
      long: 'bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600',
    };
    return bgMap[slideType];
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex flex-col transition-colors duration-500 ${type === 'daily' ? getDailyBgClass() : 'bg-black'}`}
      onClick={onClose}
    >
      {/* Progress bars */}
      <div className="absolute top-0 left-0 right-0 z-10 flex gap-1 p-2 pt-safe">
        {Array.from({ length: totalSlides }).map((_, i) => (
          <div key={i} className="flex-1 h-0.5 bg-white/30 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-100"
              style={{
                width: i < currentSlide ? '100%' : i === currentSlide ? `${progress}%` : '0%',
              }}
            />
          </div>
        ))}
      </div>

      {/* Close button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-12 right-4 z-20 p-2 text-white/80 hover:text-white transition-colors"
      >
        <X size={28} />
      </button>

      {/* Refresh button (daily guide only) */}
      {type === 'daily' && onRefreshGuide && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRefreshGuide();
          }}
          disabled={isRefreshing}
          className="absolute top-12 left-4 z-20 p-2 text-white/80 hover:text-white transition-colors"
        >
          <RefreshCw size={24} className={isRefreshing ? 'animate-spin' : ''} />
        </button>
      )}

      {/* Navigation buttons - use buttons instead of overlay areas to not block scroll */}
      <button
        className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-3 text-white/50 hover:text-white transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          handlePrevSlide();
        }}
        aria-label="Previous slide"
      >
        <ChevronLeft size={32} />
      </button>
      <button
        className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-3 text-white/50 hover:text-white transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          handleNextSlide();
        }}
        aria-label="Next slide"
      >
        <ChevronRight size={32} />
      </button>

      {/* Content - scrollable area with proper mobile support */}
      <div 
        className="flex-1 flex flex-col items-center justify-start px-4 pt-16 pb-32 overflow-y-auto overscroll-contain touch-pan-y"
        style={{ 
          WebkitOverflowScrolling: 'touch',
          paddingBottom: 'max(8rem, calc(8rem + env(safe-area-inset-bottom)))',
          paddingTop: 'max(4rem, calc(4rem + env(safe-area-inset-top)))'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Loading state when generating guide */}
        {type === 'daily' && !dailyGuide && isRefreshing && (
          <div className="text-center max-w-lg w-full px-4">
            {/* Loader icon */}
            <div className="mb-8 flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-amber-400/30 blur-3xl rounded-full"></div>
                <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center shadow-2xl shadow-amber-500/50">
                  <div className="spinner spinner-xl spinner-white" />
                </div>
              </div>
            </div>

            {/* Loading text */}
            <h2 className="text-2xl sm:text-3xl font-bold text-white leading-tight mb-4 tracking-tight">
              {t.generating}
            </h2>
            <p className="text-lg text-white/70">
              {t.generatingSubtitle}
            </p>

            {/* Static dots */}
            <div className="flex items-center justify-center gap-2 mt-8">
              <div className="w-2 h-2 rounded-full bg-white/60" />
              <div className="w-2 h-2 rounded-full bg-white/40" />
              <div className="w-2 h-2 rounded-full bg-white/20" />
            </div>
          </div>
        )}

        {type === 'daily' && dailyGuide && (
          <DailyGuideContent
            guide={dailyGuide}
            currentSlide={currentSlide}
            onCompleteAction={onCompleteAction}
            t={t}
          />
        )}
        {type === 'weekly' && weeklyRecap && (
          <WeeklyRecapContent
            recap={weeklyRecap}
            currentSlide={currentSlide}
            t={t}
            language={language}
          />
        )}
      </div>

      {/* Slide indicators - positioned above safe area */}
      <div 
        className="absolute left-0 right-0 flex justify-center gap-2 z-20"
        style={{ bottom: 'max(2rem, calc(1rem + env(safe-area-inset-bottom)))' }}
      >
        {Array.from({ length: totalSlides }).map((_, i) => (
          <div
            key={i}
            className={`w-2 h-2 rounded-full transition-colors ${
              i === currentSlide ? 'bg-white' : 'bg-white/30'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// Brutal gradient styles for each slide type
const DAILY_SLIDE_STYLES = {
  insight: {
    bg: 'from-violet-600 via-purple-600 to-fuchsia-600',
    emoji: '✨',
    pattern: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)',
  },
  short: {
    bg: 'from-amber-500 via-orange-500 to-red-500',
    emoji: '🎯',
    pattern: 'radial-gradient(circle at 80% 20%, rgba(255,255,255,0.15) 0%, transparent 40%)',
  },
  medium: {
    bg: 'from-blue-600 via-indigo-600 to-purple-700',
    emoji: '🚀',
    pattern: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1) 0%, transparent 60%)',
  },
  long: {
    bg: 'from-emerald-500 via-teal-500 to-cyan-600',
    emoji: '🌟',
    pattern: 'radial-gradient(circle at 30% 70%, rgba(255,255,255,0.12) 0%, transparent 45%)',
  },
};

// Daily Guide Content Component - Now shows one item per slide
interface DailyGuideContentProps {
  guide: DailyGuideData;
  currentSlide: number;
  onCompleteAction?: (actionType: 'short' | 'medium' | 'long', completed: boolean) => void;
  t: any;
}

const DailyGuideContent: React.FC<DailyGuideContentProps> = ({
  guide,
  currentSlide,
  onCompleteAction,
  t,
}) => {
  // Build slides array dynamically
  const slides: Array<{
    type: 'insight' | 'short' | 'medium' | 'long';
    title: string;
    subtitle: string;
    content: string;
    timeframe?: string;
    completed?: boolean;
  }> = [];

  // Slide 0: Always insight
  slides.push({
    type: 'insight',
    title: t.yourGuide,
    subtitle: guide.guideType === 'morning' ? t.morning : t.evening,
    content: guide.dailyInsight,
  });

  // Slide 1: Short term (if exists)
  if (guide.shortTermAction) {
    slides.push({
      type: 'short',
      title: t.today,
      subtitle: t.yourPlan,
      content: guide.shortTermAction,
      timeframe: guide.shortTermTimeframe,
      completed: guide.shortTermCompleted,
    });
  }

  // Slide 2: Medium term (if exists)
  if (guide.mediumTermGoal) {
    slides.push({
      type: 'medium',
      title: t.thisWeek,
      subtitle: t.yourPlan,
      content: guide.mediumTermGoal,
      timeframe: guide.mediumTermTimeframe,
      completed: guide.mediumTermCompleted,
    });
  }

  // Slide 3: Long term (if exists)
  if (guide.longTermVision) {
    slides.push({
      type: 'long',
      title: t.vision,
      subtitle: t.yourPlan,
      content: guide.longTermVision,
      timeframe: guide.longTermTimeframe,
      completed: guide.longTermCompleted,
    });
  }

  const currentSlideData = slides[currentSlide];
  if (!currentSlideData) return null;

  const style = DAILY_SLIDE_STYLES[currentSlideData.type];

  return (
    <div
      className="w-full flex items-start justify-center px-2 py-4"
      style={{ backgroundImage: style.pattern }}
    >
      <div
        key={currentSlide}
        className="max-w-md w-full animate-in fade-in zoom-in-95 duration-300"
      >
        {/* Large emoji */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-white/10 backdrop-blur-sm mb-4">
            <span className="text-5xl">{style.emoji}</span>
          </div>
          <p className="text-xs font-bold text-white/60 uppercase tracking-[0.2em]">{currentSlideData.subtitle}</p>
          <h2 className="text-2xl font-black text-white tracking-tight mt-1">{currentSlideData.title}</h2>
        </div>

        {/* Content card - Brutal style */}
        <div className="relative">
          {/* Decorative corners */}
          <div className="absolute -top-3 -left-3 w-6 h-6 border-l-4 border-t-4 border-white/30 rounded-tl-lg" />
          <div className="absolute -bottom-3 -right-3 w-6 h-6 border-r-4 border-b-4 border-white/30 rounded-br-lg" />

          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20">
            {/* Quote marks for insight */}
            {currentSlideData.type === 'insight' && (
              <div className="text-5xl text-white/20 font-serif leading-none mb-2">"</div>
            )}

            {/* Main text */}
            <p className="text-white text-xl sm:text-2xl font-bold leading-relaxed tracking-tight">
              {currentSlideData.content}
            </p>

            {/* Quote marks end for insight */}
            {currentSlideData.type === 'insight' && (
              <div className="text-5xl text-white/20 font-serif leading-none text-right mt-2">"</div>
            )}

            {/* Timeframe badge */}
            {currentSlideData.timeframe && (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full">
                <span className="text-xs font-bold text-white/70 uppercase tracking-wider">
                  {currentSlideData.timeframe}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Connected Values for insight slide */}
        {currentSlideData.type === 'insight' && guide.connectedValues && guide.connectedValues.filter(v => v && v.trim() && v.toLowerCase() !== 'undefined' && v.toLowerCase() !== 'indefinido').length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mt-6">
            {guide.connectedValues
              .filter(v => v && v.trim() && v.toLowerCase() !== 'undefined' && v.toLowerCase() !== 'indefinido')
              .slice(0, 3)
              .map((value, i) => (
                <span
                  key={i}
                  className="px-3 py-1.5 bg-white/10 backdrop-blur-md rounded-full text-xs font-medium text-white/80 border border-white/10"
                >
                  {value}
                </span>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Weekly Recap Content Component
interface WeeklyRecapContentProps {
  recap: WeeklyRecapData;
  currentSlide: number;
  t: any;
  language: Language;
}

const WeeklyRecapContent: React.FC<WeeklyRecapContentProps> = ({
  recap,
  currentSlide,
  t,
  language,
}) => {
  const formatDate = (date: Date) => {
    const locales: Record<string, string> = {
      Italian: 'it-IT',
      English: 'en-US',
      Spanish: 'es-ES',
      German: 'de-DE',
      French: 'fr-FR',
    };
    return new Intl.DateTimeFormat(locales[language] || 'en-US', {
      day: 'numeric',
      month: 'short',
    }).format(new Date(date));
  };

  if (currentSlide === 0) {
    // Oracle message slide
    return (
      <div className="text-center max-w-lg animate-in fade-in zoom-in-95 duration-300">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6">
          <span className="text-[12px] font-bold text-white/70 uppercase tracking-wider">
            {t.weeklyOracle}
          </span>
        </div>
        <p className="text-[14px] text-white/60 mb-4">
          {formatDate(recap.weekStart)} - {formatDate(recap.weekEnd)}
        </p>
        <p className="text-xl sm:text-2xl text-white leading-relaxed font-medium">
          "{recap.oracleMessage}"
        </p>
      </div>
    );
  }

  if (currentSlide === 1) {
    // Stats slide
    return (
      <div className="text-center max-w-lg animate-in fade-in slide-in-from-right-4 duration-300">
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="p-4 bg-white/10 backdrop-blur-sm rounded-2xl">
            <div className="text-4xl mb-2">{recap.dominantEmotion || '✨'}</div>
            <p className="text-[11px] text-white/60 uppercase tracking-wide">{t.dominantEmotion}</p>
          </div>
          <div className="p-4 bg-white/10 backdrop-blur-sm rounded-2xl">
            <div className="text-3xl font-bold text-white mb-1">{recap.entriesCount}</div>
            <p className="text-[11px] text-white/60 uppercase tracking-wide">{t.moments}</p>
          </div>
          <div className="p-4 bg-white/10 backdrop-blur-sm rounded-2xl">
            <div className="text-3xl font-bold text-white mb-1">{recap.weeklyScore || '--'}</div>
            <p className="text-[11px] text-white/60 uppercase tracking-wide">{t.score}</p>
          </div>
        </div>
        {recap.keyInsight && (
          <p className="text-lg text-white/90 leading-relaxed">
            {recap.keyInsight}
          </p>
        )}
      </div>
    );
  }

  // Actions slide
  return (
    <div className="w-full max-w-lg space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
      {recap.topPattern && (
        <div className="p-4 bg-amber-500/20 backdrop-blur-sm rounded-2xl border border-amber-400/30">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">🧠</span>
            <span className="text-[11px] font-bold text-amber-300 uppercase tracking-wide">
              {t.keyPattern}
            </span>
          </div>
          <p className="text-[15px] text-white/90">{recap.topPattern}</p>
        </div>
      )}
      {recap.recommendedAction && (
        <div className="p-4 bg-emerald-500/20 backdrop-blur-sm rounded-2xl border border-emerald-400/30">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">🚀</span>
            <span className="text-[11px] font-bold text-emerald-300 uppercase tracking-wide">
              {t.nextStep}
            </span>
          </div>
          <p className="text-[15px] text-white/90">{recap.recommendedAction}</p>
        </div>
      )}
    </div>
  );
};

// Story Action Item Component
interface StoryActionItemProps {
  emoji: string;
  label: string;
  action: string;
  completed?: boolean;
  onComplete?: () => void;
}

const StoryActionItem: React.FC<StoryActionItemProps> = ({
  emoji,
  label,
  action,
  completed,
  onComplete,
}) => (
  <div
    onClick={(e) => {
      e.stopPropagation();
      onComplete?.();
    }}
    className={`flex items-start gap-3 p-4 rounded-2xl backdrop-blur-sm border transition-colors cursor-pointer ${
      completed
        ? 'bg-emerald-500/20 border-emerald-400/30'
        : 'bg-white/10 border-white/20 hover:bg-white/15'
    }`}
  >
    <div
      className={`mt-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
        completed
          ? 'bg-emerald-500 border-emerald-500 text-white'
          : 'border-white/50'
      }`}
    >
      {completed && <Check size={14} />}
    </div>
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-lg">{emoji}</span>
        <span className="text-[11px] font-bold text-white/60 uppercase tracking-wide">
          {label}
        </span>
      </div>
      <p className={`text-[15px] text-white/90 leading-snug ${completed ? 'line-through opacity-60' : ''}`}>
        {action}
      </p>
    </div>
  </div>
);

export default StoryModal;
